import { useState, useRef, useEffect, useMemo } from "react";
import { cn } from "@/utils/cn";

// 编辑模式类型
type EditMode = "click" | "doubleClick";

interface TextEditableProps {
  value: string;
  className?: string;
  placeholder?: string;
  isEditing?: boolean;
  mode?: EditMode;
  onChange: (newName: string) => void;
  onStartEditing?: () => void;
  onEndEditing?: () => void;
}

export function TextEditable({
  value,
  className,
  placeholder,
  isEditing: externalIsEditing,
  mode = "click",
  onChange,
  onStartEditing,
  onEndEditing,
}: TextEditableProps) {
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isEditing = useMemo(() => {
    return externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;
  }, [externalIsEditing, internalIsEditing]);
  const isControlled = externalIsEditing !== undefined;

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSubmit = () => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue && trimmedValue !== value) {
      onChange(trimmedValue);
    } else {
      setInputValue(value);
    }

    // 无论是否受控，都应该结束编辑状态
    if (!isControlled) {
      setInternalIsEditing(false);
    }
    // 通知父组件编辑已结束
    onEndEditing?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmit();
    } else if (e.key === "Escape") {
      setInputValue(value);
      if (!isControlled) {
        setInternalIsEditing(false);
      }
      onEndEditing?.();
    }
  };

  const handleBlur = () => {
    handleSubmit();
  };

  const startEditing = () => {
    onStartEditing?.();
    if (!isControlled) {
      setInternalIsEditing(true);
    }
  };

  // 处理单击事件（延迟执行，如果在延迟期间发生双击则取消）
  const handleSingleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }

    clickTimeoutRef.current = setTimeout(() => {
      if (!isEditing) {
        // 向父元素抛出点击事件
        const syntheticEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          clientX: e.clientX,
          clientY: e.clientY,
        });
        if (e.target && "parentElement" in e.target && e.target.parentElement instanceof HTMLElement) {
          e.target.parentElement.dispatchEvent(syntheticEvent);
        }
      }
    }, 200); // 200ms延迟
  };

  // 处理双击事件
  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    // 清除单击的定时器
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }

    startEditing();
  };

  const eventProps = mode === "click"
    ? { onClick: startEditing }
    : { onDoubleClick: handleDoubleClick, onClick: handleSingleClick };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        className={cn(
          "text-sm font-semibold text-i-normal bg-i-white",
          "rounded-sm px-2 py-1",
          "focus:outline-none",
          className
        )}
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onClick={e => e.stopPropagation()}
      />
    );
  }

  return (
    <button
      {...eventProps}
      className={cn(
        "text-sm font-semibold text-i-norma",
        "rounded-sm px-2 py-1",
        "text-left truncate",
        className
      )}
    >
      {value || placeholder}
    </button>
  );
}
